import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Eye, EyeSlash } from "@phosphor-icons/react";
import config from "../config";

const API = `${config.BACKEND_URL}/api`;

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Gmail only validation
    if (!email.endsWith("@gmail.com")) {
      setError("Only Gmail accounts are allowed (@gmail.com)");
      return;
    }

    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/dashboard");
      } else if (mode === "register") {
        if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
        await register(email, password, name);
        setSuccess("✅ Account created! Please check your Gmail inbox to verify your account.");
        setMode("login");
        setEmail("");
        setPassword("");
        setName("");
      } else if (mode === "forgot") {
        const res = await fetch(`${API}/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        setSuccess("✅ If this Gmail exists, a reset link has been sent. Check your inbox!");
        setMode("login");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white grid-bg flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#0A0A0A] mb-8 transition-colors"
          data-testid="auth-back-link"
        >
          <ArrowLeft size={16} weight="bold" />
          Back to home
        </Link>

        <div className="border border-[#E5E7EB] bg-white p-8">
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-black tracking-tight text-[#0A0A0A]">
              {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Forgot Password"}
            </h1>
            <p className="text-sm text-[#6B7280] mt-2">
              {mode === "login"
                ? "Welcome back. Enter your credentials."
                : mode === "register"
                ? "Get started building AI chatbots."
                : "Enter your Gmail to receive a reset link."}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 mb-6" data-testid="auth-error">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 text-sm px-4 py-3 mb-6">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">
                  Full Name
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  className="rounded-none border-[#D1D5DB] focus:border-[#002FA7] focus:ring-[#002FA7]"
                  data-testid="auth-name-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gmail.com"
                required
                className="rounded-none border-[#D1D5DB] focus:border-[#002FA7] focus:ring-[#002FA7]"
                data-testid="auth-email-input"
              />
              {mode === "register" && (
                <p className="text-xs text-[#6B7280]">⚠️ Only Gmail accounts are allowed</p>
              )}
            </div>

            {mode !== "forgot" && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/60">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    required
                    minLength={6}
                    className="rounded-none border-[#D1D5DB] focus:border-[#002FA7] focus:ring-[#002FA7] pr-10"
                    data-testid="auth-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0A0A0A]"
                    data-testid="auth-toggle-password"
                  >
                    {showPw ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            {mode === "login" && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setMode("forgot"); setError(""); setSuccess(""); }}
                  className="text-xs text-[#002FA7] hover:text-[#001F73]"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white font-semibold h-11 transition-transform active:scale-[0.98]"
              data-testid="auth-submit-button"
            >
              {loading
                ? "Please wait..."
                : mode === "login"
                ? "Sign in"
                : mode === "register"
                ? "Create account"
                : "Send Reset Link"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-[#E5E7EB] text-center space-y-2">
            {mode === "forgot" ? (
              <button
                onClick={() => { setMode("login"); setError(""); setSuccess(""); }}
                className="text-sm text-[#002FA7] hover:text-[#001F73] font-medium"
              >
                ← Back to Sign in
              </button>
            ) : (
              <button
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setSuccess(""); }}
                className="text-sm text-[#002FA7] hover:text-[#001F73] font-medium"
                data-testid="auth-toggle-mode"
              >
                {mode === "login" ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
